/**
 * MbProduct
 *
 * Gestisce:
 * - Cambio variante tramite Shopify Section Rendering API.
 * - Aggiunta al carrello con controllo delle quantità.
 * - Conferma permanente per il pezzo unico presente nel carrello.
 * - Conferma temporanea per prodotti con più pezzi disponibili.
 * - Sincronizzazione dopo modifiche e rimozioni dal carrello.
 */
class MbProduct {
    constructor() {
        this.container = document.querySelector('[data-mb-product-hero]');
        if (!this.container) return;

        this.sectionId = this.container.dataset.sectionId;
        this.cartSections = ['mb-cart-drawer', 'mb-cart', 'header'];

        this.selectors = [
            '[data-mb-price-current]',
            '[data-mb-variant-id]',
            '[data-mb-atc]',
            '[data-mb-atc-text]',
            '[data-mb-condition-text]',
            '[data-mb-condition-features]',
            '[data-mb-product-inv]'
        ];

        this.cart = null;
        this.buttonStates = new WeakMap();
        this.addingForms = new Set();

        this.successDuration = 2500;
        this.successTimers = new Set();

        this.variantLoading = false;
        this.variantSequence = 0;
        this.cartSequence = 0;

        this.variantController = null;
        this.cartController = null;
        this.destroyed = false;

        this.init();
    }

    init() {
        this.onVariantChange = (event) => {
            const variantId = event.detail?.variantId;
            if (!variantId) return;

            this.renderProductInfo(variantId);
            this.updateBrowserURL(variantId);
        };

        this.onSubmit = (event) => {
            const form = event.target.closest('[data-mb-product-form]');
            if (!form) return;

            event.preventDefault();
            this.handleAddToCart(form);
        };

        this.onCartChanged = () => {
            // Il payload può contenere solo la riga aggiunta:
            // rileggiamo il carrello completo.
            this.invalidateCartRead();
            this.scheduleCartSync();
        };

        this.onReturn = () => {
            if (!document.hidden) {
                this.scheduleCartSync(0);
            }
        };

        this.container.addEventListener(
            'mb:variant:change',
            this.onVariantChange
        );

        document.addEventListener('submit', this.onSubmit);
        document.addEventListener('mb:cart:changed', this.onCartChanged);
        document.addEventListener('visibilitychange', this.onReturn);

        window.addEventListener('focus', this.onReturn);
        window.addEventListener('pageshow', this.onReturn);

        // Controllo di riserva per modifiche del carrello
        // che non emettono l'evento del tema.
        this.pollTimer = window.setInterval(() => {
            if (!document.hidden && !this.cartController) {
                this.scheduleCartSync(0);
            }
        }, 5000);

        this.refreshButtons();
        this.scheduleCartSync(0);
    }

    destroy() {
        this.destroyed = true;

        this.container?.removeEventListener(
            'mb:variant:change',
            this.onVariantChange
        );

        document.removeEventListener('submit', this.onSubmit);
        document.removeEventListener('mb:cart:changed', this.onCartChanged);
        document.removeEventListener('visibilitychange', this.onReturn);

        window.removeEventListener('focus', this.onReturn);
        window.removeEventListener('pageshow', this.onReturn);

        window.clearInterval(this.pollTimer);
        window.clearTimeout(this.cartSyncTimer);

        this.successTimers.forEach((timer) => {
            window.clearTimeout(timer);
        });

        this.successTimers.clear();

        this.variantController?.abort();
        this.invalidateCartRead();
    }

    getRootUrl() {
        return window.Shopify?.routes?.root || '/';
    }

    getAddedLabel() {
        const language = (
            document.documentElement.lang ||
            window.Shopify?.locale ||
            'it'
        )
            .toLowerCase()
            .split(/[-_]/)[0];

        const labels = {
            it: 'Aggiunto al carrello',
            ro: 'Adăugat în coș',
            en: 'Added to cart',
            de: 'Zum Warenkorb hinzugefügt',
            fr: 'Ajouté au panier',
            es: 'Añadido al carrito'
        };

        return labels[language] || labels.en;
    }

    getInventory(form) {
        const scope = form?.closest('[data-mb-product-hero]');
        const inventoryElement = scope?.querySelector(
            '[data-mb-product-inv]'
        );

        if (!inventoryElement) return null;

        try {
            return JSON.parse(inventoryElement.textContent);
        } catch (error) {
            console.warn('Invalid product inventory JSON:', error);
            return null;
        }
    }

    isProductWideUnique(inventory) {
        return (
            inventory?.isSingleStock === true ||
            inventory?.isSingleStock === 'true'
        );
    }

    isSingleVariantStock(inventory) {
        return Boolean(
            inventory &&
            inventory.management &&
            inventory.policy === 'deny' &&
            Number(inventory.quantity) === 1
        );
    }

    getVariantQuantity(cart, variantId) {
        if (!Array.isArray(cart?.items)) return 0;

        return cart.items.reduce((total, item) => {
            const itemVariantId = item.variant_id ?? item.id;

            if (String(itemVariantId) !== String(variantId)) {
                return total;
            }

            return total + Math.max(0, Number(item.quantity) || 0);
        }, 0);
    }

    isUniqueProductInCart(cart, inventory, variantId) {
        if (!Array.isArray(cart?.items)) return false;

        // Mantiene la regola esistente:
        // un pezzo unico non può essere acquistato in due varianti diverse.
        if (
            this.isProductWideUnique(inventory) &&
            inventory.productHandle
        ) {
            return cart.items.some((item) => {
                return (
                    item.handle === inventory.productHandle &&
                    Number(item.quantity) > 0
                );
            });
        }

        if (
            this.isProductWideUnique(inventory) ||
            this.isSingleVariantStock(inventory)
        ) {
            return this.getVariantQuantity(cart, variantId) > 0;
        }

        return false;
    }

    getButtonState(button, variantId) {
        let state = this.buttonStates.get(button);

        if (!state || state.variantId !== String(variantId)) {
            state = {
                variantId: String(variantId),
                normalText: button.querySelector(
                    '[data-mb-atc-text]'
                )?.textContent.trim() || '',
                successUntil: 0
            };

            this.buttonStates.set(button, state);
        }

        return state;
    }

    refreshButtons() {
        if (this.destroyed) return;

        document.querySelectorAll('[data-mb-product-form]').forEach((form) => {
            this.refreshButton(form);
        });
    }

    refreshButton(form) {
        const button = form.querySelector('[data-mb-atc]');
        const text = button?.querySelector('[data-mb-atc-text]');
        const variantId = form.querySelector('[data-mb-variant-id]')?.value;

        if (!button || !text || !variantId) return;

        const state = this.getButtonState(button, variantId);
        const inventory = this.getInventory(form);

        const unavailable = (
            button.classList.contains('is-disabled') ||
            button.disabled
        );

        const changingVariant = (
            this.variantLoading &&
            this.container.contains(form)
        );

        const busy = this.addingForms.has(form) || changingVariant;

        const permanentlyAdded = (
            !unavailable &&
            this.isUniqueProductInCart(
                this.cart,
                inventory,
                variantId
            )
        );

        const temporarilyAdded = (
            !unavailable &&
            Date.now() < state.successUntil
        );

        const showSuccess = permanentlyAdded || temporarilyAdded;

        button.classList.toggle('is-success', showSuccess);
        button.classList.toggle('is-cart-locked', permanentlyAdded);

        button.setAttribute('aria-busy', String(busy));

        button.setAttribute(
            'aria-disabled',
            String(unavailable || busy || showSuccess)
        );

        text.textContent = showSuccess
            ? this.getAddedLabel()
            : state.normalText;
    }

    showTemporarySuccess(form, variantId) {
        const button = form.querySelector('[data-mb-atc]');
        const currentVariantId = form.querySelector(
            '[data-mb-variant-id]'
        )?.value;

        // Una richiesta iniziata sulla variante precedente non deve
        // cambiare la conferma della nuova variante selezionata.
        if (
            !button ||
            this.variantLoading ||
            String(currentVariantId) !== String(variantId)
        ) {
            return;
        }

        const state = this.getButtonState(button, variantId);
        state.successUntil = Date.now() + this.successDuration;

        this.refreshButton(form);

        const timer = window.setTimeout(() => {
            this.successTimers.delete(timer);

            if (form.isConnected && !this.destroyed) {
                this.refreshButton(form);
            }
        }, this.successDuration + 30);

        this.successTimers.add(timer);
    }

    invalidateCartRead() {
        this.cartSequence += 1;
        this.cartController?.abort();
        this.cartController = null;
    }

    scheduleCartSync(delay = 120) {
        window.clearTimeout(this.cartSyncTimer);

        if (this.destroyed || document.hidden) return;

        this.cartSyncTimer = window.setTimeout(() => {
            this.syncCart();
        }, delay);
    }

    async fetchCart(signal) {
        const response = await fetch(`${this.getRootUrl()}cart.js`, {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {
                Accept: 'application/json'
            },
            signal
        });

        if (!response.ok) {
            throw new Error('Unable to read cart');
        }

        const cart = await response.json();

        if (!Array.isArray(cart.items)) {
            throw new Error('Invalid cart response');
        }

        return cart;
    }

    async syncCart() {
        if (
            this.destroyed ||
            document.hidden ||
            this.addingForms.size > 0
        ) {
            return;
        }

        this.invalidateCartRead();

        const controller = new AbortController();
        const sequence = this.cartSequence;

        this.cartController = controller;

        try {
            const cart = await this.fetchCart(controller.signal);

            if (
                this.destroyed ||
                sequence !== this.cartSequence ||
                this.addingForms.size > 0
            ) {
                return;
            }

            this.cart = cart;
            this.refreshButtons();
        } catch (error) {
            // Mantiene l'ultimo stato noto.
            // Il prossimo evento o controllo periodico riproverà.
        } finally {
            if (this.cartController === controller) {
                this.cartController = null;
            }
        }
    }

    /**
     * Aggiorna i dati della variante mantenendo i nodi principali.
     * Le richieste superate da un nuovo cambio variante vengono annullate.
     */
    async renderProductInfo(variantId) {
        this.variantController?.abort();

        const controller = new AbortController();
        const sequence = ++this.variantSequence;

        this.variantController = controller;
        this.variantLoading = true;

        this.refreshButtons();
        this.toggleQtyLimitMessage(false);

        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variantId));
        url.searchParams.set('section_id', this.sectionId);

        try {
            const response = await fetch(url.toString(), {
                signal: controller.signal
            });

            if (!response.ok) {
                throw new Error('Unable to load product variant');
            }

            const htmlText = await response.text();

            if (
                this.destroyed ||
                sequence !== this.variantSequence
            ) {
                return;
            }

            const fetchedHtml = new DOMParser().parseFromString(
                htmlText,
                'text/html'
            );

            this.selectors.forEach((selector) => {
                const newElement = fetchedHtml.querySelector(selector);
                const currentElement = this.container.querySelector(selector);

                if (!newElement || !currentElement) return;

                if (currentElement.tagName === 'INPUT') {
                    currentElement.value = newElement.value;
                } else {
                    if (selector === '[data-mb-atc]') {
                        // Il testo originale deve essere quello della
                        // nuova variante, anche quando è esaurita.
                        this.buttonStates.delete(currentElement);
                    }

                    currentElement.innerHTML = newElement.innerHTML;
                    currentElement.className = newElement.className;

                    if (selector === '[data-mb-atc]') {
                        currentElement.disabled = newElement.disabled;
                    }
                }
            });
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Shopify Section API error:', error);
            }
        } finally {
            if (sequence === this.variantSequence) {
                this.variantLoading = false;
                this.variantController = null;

                this.refreshButtons();
                this.scheduleCartSync(0);
            }
        }
    }

    /**
     * Controlla la quantità richiesta sommando tutte le righe
     * della stessa variante, anche con proprietà differenti.
     */
    canAddAnother(variantId, inventory, cart, requestedQuantity = 1) {
        if (!inventory) return true;

        if (
            this.isProductWideUnique(inventory) &&
            inventory.productHandle
        ) {
            const alreadyInCart = cart.items.some((item) => {
                return (
                    item.handle === inventory.productHandle &&
                    Number(item.quantity) > 0
                );
            });

            if (alreadyInCart || requestedQuantity > 1) {
                return false;
            }
        } else if (this.isProductWideUnique(inventory)) {
            if (
                this.getVariantQuantity(cart, variantId) > 0 ||
                requestedQuantity > 1
            ) {
                return false;
            }
        }

        const isTracked = Boolean(inventory.management);
        const deniesOversell = inventory.policy === 'deny';

        if (!isTracked || !deniesOversell) return true;

        const availableQuantity = Number(inventory.quantity);

        // Se manca un dato valido, Shopify applicherà comunque
        // il controllo definitivo durante l'aggiunta.
        if (!Number.isFinite(availableQuantity)) return true;

        const inCart = this.getVariantQuantity(cart, variantId);

        return inCart + requestedQuantity <= availableQuantity;
    }

    async handleAddToCart(form) {
        const button = form.querySelector('[data-mb-atc]');
        const variantId = form.querySelector('[data-mb-variant-id]')?.value;

        if (!button || !variantId || this.destroyed) return;

        this.refreshButton(form);

        if (
            button.disabled ||
            button.classList.contains('is-disabled') ||
            button.classList.contains('is-success') ||
            this.addingForms.size > 0 ||
            (this.variantLoading && this.container.contains(form))
        ) {
            return;
        }

        // Cattura i dati al momento del clic, prima delle richieste.
        const inventory = this.getInventory(form);
        const formData = new FormData(form);
        const requestedQuantity = Number(formData.get('quantity') || 1);

        formData.set('id', variantId);
        formData.set('sections', this.cartSections.join(','));
        formData.set(
            'sections_url',
            window.location.pathname + window.location.search
        );

        this.addingForms.add(form);
        this.invalidateCartRead();
        this.refreshButton(form);
        this.toggleQtyLimitMessage(false, form);

        let addedData = null;

        try {
            if (
                !Number.isInteger(requestedQuantity) ||
                requestedQuantity < 1
            ) {
                this.toggleQtyLimitMessage(true, form);
                return;
            }

            const cart = await this.fetchCart();

            if (this.destroyed) return;

            this.cart = cart;

            const canAdd = this.canAddAnother(
                variantId,
                inventory,
                cart,
                requestedQuantity
            );

            if (!canAdd) {
                // Se il pezzo unico è già nel carrello, mostra
                // direttamente la conferma permanente.
                const alreadyAdded = this.isUniqueProductInCart(
                    cart,
                    inventory,
                    variantId
                );

                this.toggleQtyLimitMessage(!alreadyAdded, form);
                this.refreshButtons();
                return;
            }

            const response = await fetch(
                `${this.getRootUrl()}cart/add.js`,
                {
                    method: 'POST',
                    credentials: 'same-origin',
                    body: formData,
                    headers: {
                        Accept: 'application/json'
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.description ||
                    data.message ||
                    'Error adding to cart'
                );
            }

            addedData = data;

            if (!this.destroyed) {
                // Aggiornamento locale immediato per la conferma.
                // Subito dopo verrà riletto il carrello completo.
                this.cart = {
                    ...cart,
                    items: [
                        ...cart.items,
                        {
                            id: variantId,
                            variant_id: variantId,
                            handle: inventory?.productHandle || '',
                            quantity: requestedQuantity
                        }
                    ]
                };

                this.showTemporarySuccess(form, variantId);
                this.refreshButtons();
            }
        } catch (error) {
            console.error('ATC Error:', error);

            if (!this.destroyed) {
                alert(error.message);
            }
        } finally {
            this.addingForms.delete(form);

            if (!this.destroyed) {
                this.refreshButtons();
                this.scheduleCartSync(0);
            }
        }

        if (!addedData) return;

        // Conserva l'integrazione esistente con il carrello del tema.
        document.dispatchEvent(
            new CustomEvent('mb:cart:changed', {
                detail: { cart: addedData }
            })
        );

        try {
            if (typeof window.mbCart?.open === 'function') {
                window.mbCart.open();
            } else {
                document
                    .querySelector('[data-mb-cart-drawer]')
                    ?.setAttribute('aria-open', 'true');
            }
        } catch (error) {
            // L'aggiunta è già riuscita: un problema del drawer
            // non deve essere mostrato come errore di acquisto.
            console.warn('Unable to open cart drawer:', error);
        }
    }

    toggleQtyLimitMessage(show, form = null) {
        const scope = form?.closest('[data-mb-product-purchase]') ||
            form?.closest('[data-mb-product-hero]') ||
            this.container;

        const message = scope?.querySelector('.max-qty-warning');

        if (message) {
            message.setAttribute(
                'aria-hidden',
                show ? 'false' : 'true'
            );
        }
    }

    updateBrowserURL(variantId) {
        const url = new URL(window.location.href);

        url.searchParams.set('variant', String(variantId));

        window.history.replaceState({}, '', url.toString());
    }
}

// Inizializzazione compatibile anche con script caricati dopo il DOM.
(function () {
    function initializeMbProduct() {
        if (typeof window.mbProductInstance?.destroy === 'function') {
            window.mbProductInstance.destroy();
        }

        window.mbProductInstance = new MbProduct();
    }

    if (document.readyState === 'loading') {
        document.addEventListener(
            'DOMContentLoaded',
            initializeMbProduct,
            { once: true }
        );
    } else {
        initializeMbProduct();
    }
})();